import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { EntityContacts } from "@/components/admin/EntityContacts";
import { HaulierVehicleTypes } from "@/components/admin/HaulierVehicleTypes";
import { CheckboxField, TextField } from "@/components/admin/fields";
import {
  createHaulier,
  deleteHaulier,
  getHaulier,
  listHauliers,
  updateHaulier,
} from "@/services/api/hauliers";
import type { Haulier } from "@/services/api/types";

interface HaulierForm {
  haulier_code: string;
  name: string;
  is_active: boolean;
  phone: string;
  address: string;
  postcode: string;
  account_manager_name: string;
  account_manager_email: string;
  account_manager_phone: string;
}

const emptyForm: HaulierForm = {
  haulier_code: "",
  name: "",
  is_active: true,
  phone: "",
  address: "",
  postcode: "",
  account_manager_name: "",
  account_manager_email: "",
  account_manager_phone: "",
};

export function HauliersPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const query = useQuery({
    queryKey: ["hauliers", offset],
    queryFn: () => listHauliers({ limit, offset }),
  });

  const columns: ColumnDef<Haulier>[] = [
    { accessorKey: "haulier_code", header: t("admin.haulierCode") },
    { accessorKey: "name", header: t("admin.name") },
    {
      accessorKey: "is_active",
      header: t("admin.active"),
      cell: ({ getValue }) => (getValue() ? t("common.yes") : t("common.no")),
    },
  ];

  return (
    <FullCrudPage<Haulier, HaulierForm>
      title={t("nav.hauliers")}
      entityType="haulier"
      getRecord={getHaulier}
      columns={columns}
      data={query.data?.items ?? []}
      total={query.data?.total ?? 0}
      isLoading={query.isLoading}
      limit={limit}
      offset={offset}
      onPageChange={setOffset}
      queryKey="hauliers"
      emptyForm={emptyForm}
      toForm={(r) => ({
        haulier_code: r.haulier_code,
        name: r.name,
        is_active: r.is_active,
        phone: r.phone ?? "",
        address: r.address ?? "",
        postcode: r.postcode ?? "",
        account_manager_name: r.account_manager_name ?? "",
        account_manager_email: r.account_manager_email ?? "",
        account_manager_phone: r.account_manager_phone ?? "",
      })}
      canSubmit={(f) => Boolean(f.haulier_code && f.name)}
      renderForm={(form, setForm) => (
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("admin.haulierCode")}
            value={form.haulier_code}
            onChange={(v) => setForm({ ...form, haulier_code: v })}
            required
          />
          <TextField
            label={t("admin.name")}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <CheckboxField
            label={t("admin.active")}
            checked={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: v })}
          />
          <TextField
            label={t("admin.phone")}
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
          />
          <TextField
            label={t("admin.address")}
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
          />
          <TextField
            label={t("admin.postcode")}
            value={form.postcode}
            onChange={(v) => setForm({ ...form, postcode: v })}
          />
          <TextField
            label={t("admin.accountManagerName")}
            value={form.account_manager_name}
            onChange={(v) => setForm({ ...form, account_manager_name: v })}
          />
          <TextField
            label={t("admin.accountManagerEmail")}
            value={form.account_manager_email}
            onChange={(v) => setForm({ ...form, account_manager_email: v })}
          />
          <TextField
            label={t("admin.accountManagerPhone")}
            value={form.account_manager_phone}
            onChange={(v) => setForm({ ...form, account_manager_phone: v })}
          />
        </div>
      )}
      onSave={async (mode, form, record) =>
        mode === "edit" && record
          ? updateHaulier(record.id, form)
          : createHaulier(form)
      }
      onDelete={(record) => deleteHaulier(record.id)}
      recordTitle={(r) => r.haulier_code}
      detailFields={(r) => [
        { label: t("admin.haulierCode"), value: r.haulier_code, field: "haulier_code" },
        { label: t("admin.name"), value: r.name, field: "name" },
        {
          label: t("admin.active"),
          value: r.is_active ? t("common.yes") : t("common.no"),
          field: "is_active",
        },
        { label: t("admin.phone"), value: r.phone, field: "phone" },
        { label: t("admin.address"), value: r.address, field: "address" },
        { label: t("admin.postcode"), value: r.postcode, field: "postcode" },
        {
          label: t("admin.accountManagerName"),
          value: r.account_manager_name,
          field: "account_manager_name",
        },
        {
          label: t("admin.accountManagerEmail"),
          value: r.account_manager_email,
          field: "account_manager_email",
        },
        {
          label: t("admin.accountManagerPhone"),
          value: r.account_manager_phone,
          field: "account_manager_phone",
        },
      ]}
      viewExtras={(r) => (
        <div className="space-y-4">
          <HaulierVehicleTypes haulierId={r.id} />
          <EntityContacts ownerType="haulier" ownerId={r.id} readOnly />
        </div>
      )}
      editExtras={(r) => (
        <div className="space-y-4">
          {r ? <HaulierVehicleTypes haulierId={r.id} /> : null}
          <EntityContacts ownerType="haulier" ownerId={r ? r.id : null} />
        </div>
      )}
    />
  );
}
